/**
 * Turns Lite components into ComponentInput payloads the Workspace API stores the way Lite shows them.
 *
 * Ported from:
 *  - vendor-ref/open-in-nanome-2/pipeline.js:272-291 remapFilters (entrySerial remapped everywhere, also inside
 *    Union/Intersect/Except containedFilters), :305-312 restComponent (a visualization without a size gets NoSize),
 *    :314-332 componentsToSend (a component whose visualizations are all off is sent on and hidden afterwards)
 *  - vendor-ref/v1-parser-tool/nanome_v1/mapping.py:94-110 range_set, :238-285 evaluate_filters (pipeline
 *    semantics of AtomFilteringVisitor), :288-313 _candidate_filters, :327-349 build_filters (readable filters first,
 *    each verified against the model, EnumeratedAtoms fallback), :352-390 sdf_filters (SDF atoms have no name,
 *    residue or chain on the server: SdfModelExtractor.cs:37-41, LoadSdfService.cs:153-154)
 *
 * Wire rules (research open-in-nanome-bridge__05.md): enums as strings, colours 'RRGGBB' without '#', `size` and
 * `coloring` present on every visualization, serial sets as {ranges:[[a,b]], sets:[n]}. The server's JSON Schema
 * (GET /schemas) adds `additionalProperties:false` per kind, so fields a kind does not own are stripped and the
 * fields it requires are filled with the server defaults (dto.ts VISUALIZATION_DEFAULTS).
 */
import type { ColoringModel, Component, ComponentInput, Entry, FilterModel, RangeSet, VisualizationModel } from '@nanome-lite/state';
import { NO_SIZE, VISUALIZATION_DEFAULTS, filterEntrySerials } from '@nanome-lite/state';
import type { NanomeModel } from '@nanome-lite/data';
import { Substructure } from '@nanome-lite/data/model';
import { BridgeError } from './errors';

// --- range sets (mapping.py:94-110, :223-227) ---------------------------------------------------------------------

/** v2 RangeSet: inclusive [start, end] pairs plus loose singles (mapping.py:94-110). */
export function rangeSet(serials: Iterable<number>): RangeSet {
  const ordered = [...new Set(serials)].sort((a, b) => a - b);
  const ranges: [number, number][] = [];
  const sets: number[] = [];
  let start: number | null = null;
  let prev = 0;
  const flush = () => {
    if (start === null) return;
    if (prev > start) ranges.push([start, prev]); else sets.push(start);
  };
  for (const v of ordered) {
    if (start === null) { start = prev = v; continue; }
    if (v === prev + 1) { prev = v; continue; }
    flush();
    start = prev = v;
  }
  flush();
  return { ranges, sets };
}

export function expandRangeSet(rs: RangeSet | undefined): Set<number> {
  const out = new Set<number>(rs?.sets ?? []);
  for (const r of rs?.ranges ?? []) {
    const [a, b] = r;
    for (let v = a; v <= b; v++) out.add(v);
  }
  return out;
}

// --- remap (pipeline.js:272-291) -------------------------------------------------------------------------------------

/** Every entrySerial (also inside containedFilters) replaced through `serials`, local -> server. Throws on an unknown entry. */
export function remapFilters(filters: FilterModel[], serials: Map<number, number>, where = 'filters'): FilterModel[] {
  return filters.map((filter, i) => {
    const out: FilterModel = { ...filter };
    if (filter.entrySerial !== undefined) {
      const serial = serials.get(filter.entrySerial);
      if (serial === undefined) {
        throw new BridgeError('components', `${where}[${i}] selects entry ${filter.entrySerial}, which is not one of the ${serials.size} loaded structures`);
      }
      out.entrySerial = serial;
    }
    if (filter.containedFilters) out.containedFilters = remapFilters(filter.containedFilters, serials, `${where}[${i}].containedFilters`);
    return out;
  });
}

// --- evaluation over a NanomeModel (mapping.py:238-285) --------------------------------------------------------------

export interface EvalContext { entrySerial: number; currentModelSerial: number }

/** Thrown for kinds that need server data (ConnectedResidue, Distance, ...): the component is then sent as it is. */
export class NotEvaluable extends Error { constructor(kind: string) { super(`filter kind ${kind} cannot be evaluated locally`); } }

const SUBSTRUCTURE_SETS: Record<string, readonly number[]> = {
  Protein: [Substructure.Protein, Substructure.NonStandardProtein],
  Nucleic: [Substructure.Nucleic, Substructure.NonStandardNucleic],
  DNA: [Substructure.Nucleic, Substructure.NonStandardNucleic],
  RNA: [Substructure.Nucleic, Substructure.NonStandardNucleic],
  PNA: [Substructure.Nucleic, Substructure.NonStandardNucleic],
  Polymer: [Substructure.Protein, Substructure.NonStandardProtein, Substructure.Nucleic, Substructure.NonStandardNucleic],
  Ligand: [Substructure.Ligand],
  Saccharide: [Substructure.Sugar],
  Solvent: [Substructure.Solvent],
  Ion: [Substructure.Ion],
};

function substructureTypes(flags: string | undefined): Set<number> {
  const out = new Set<number>();
  for (const part of (flags ?? '').split(',')) {
    const key = part.trim();
    for (const t of SUBSTRUCTURE_SETS[key] ?? []) out.add(t);
  }
  return out;
}

/** AltLocs Highest/Lowest: per residue + atom name, keep the one alternate with the highest/lowest occupancy (first wins ties). */
function altLocFilter(model: NanomeModel, selected: Set<number>, which: 'Highest' | 'Lowest'): Set<number> {
  const best = new Map<string, number>();
  for (const i of selected) {
    if (!model.altLoc[i]) continue;
    const key = `${model.residueIndex[i]}|${model.atomName[i]}`;
    const cur = best.get(key);
    if (cur === undefined) { best.set(key, i); continue; }
    const o = model.occupancy[i]!, c = model.occupancy[cur]!;
    if (which === 'Highest' ? o > c : o < c) best.set(key, i);
  }
  const keep = new Set(best.values());
  const out = new Set<number>();
  for (const i of selected) if (!model.altLoc[i] || keep.has(i)) out.add(i);
  return out;
}

/**
 * Atom indices (0-based into `model`) a filter list selects. A filter list is a pipeline: it starts from every atom and
 * each filter narrows; Union/Intersect/Except run containedFilters as a fresh pipeline from every atom and combine
 * (mapping.py:240-252; ApiController.cs:3130-3133). Throws NotEvaluable for kinds that need server data.
 */
export function evaluateFilters(filters: FilterModel[], model: NanomeModel, ctx: EvalContext): Set<number> {
  const n = model.atomCount;
  let selected = new Set<number>();
  for (let i = 0; i < n; i++) selected.add(i);
  const narrow = (keep: (i: number) => boolean) => { const out = new Set<number>(); for (const i of selected) if (keep(i)) out.add(i); selected = out; };
  for (const f of filters) {
    switch (f.kind) {
      case 'EntrySerial':
        if (f.entrySerial !== ctx.entrySerial) selected = new Set();
        break;
      case 'CurrentModel':
        break; // the model's xyz is the current frame already
      case 'ModelSerials':
        if (!expandRangeSet(f.serialRangeSet).has(ctx.currentModelSerial)) selected = new Set();
        break;
      case 'Substructure': {
        const types = substructureTypes(f.substructureType);
        narrow(i => types.has(model.substructure[i]!));
        break;
      }
      case 'ChainNames': {
        const names = new Set(f.names ?? []);
        narrow(i => names.has(model.chains.name[model.chainIndex[i]!]!));
        break;
      }
      case 'ResidueNames': {
        const names = new Set(f.names ?? []);
        narrow(i => names.has(model.residues.name[model.residueIndex[i]!]!));
        break;
      }
      case 'AtomNames': {
        const names = new Set(f.names ?? []);
        narrow(i => names.has(model.atomName[i]!));
        break;
      }
      case 'ResidueSerials': {
        const wanted = expandRangeSet(f.serialRangeSet);
        narrow(i => wanted.has(model.residues.serial[model.residueIndex[i]!]!));
        break;
      }
      case 'Residues': {
        const wanted = new Set((f.residues ?? []).map(r => residueKey(r.residueSerial, insertionCodeToString(r.insertionCode))));
        narrow(i => { const r = model.residueIndex[i]!; return wanted.has(residueKey(model.residues.serial[r]!, model.residues.insCode[r] ?? '')); });
        break;
      }
      case 'EnumeratedAtoms': {
        const wanted = expandRangeSet(f.serialRangeSet);
        narrow(i => wanted.has(model.atomSerial[i]!));
        break;
      }
      case 'AltLocs':
        if (f.occupancySelection === 'Highest' || f.occupancySelection === 'Lowest') selected = altLocFilter(model, selected, f.occupancySelection);
        break;
      case 'Union': case 'Intersect': case 'Except': {
        const inner = evaluateFilters(f.containedFilters ?? [], model, ctx);
        if (f.kind === 'Union') for (const i of inner) selected.add(i);
        else if (f.kind === 'Intersect') narrow(i => inner.has(i));
        else narrow(i => !inner.has(i));
        break;
      }
      default:
        throw new NotEvaluable(f.kind);
    }
  }
  return selected;
}

const residueKey = (serial: number, insCode: string) => `${serial}|${insCode.trim()}`;

/** FilterModel.Residue.InsertionCode is a byte holding the char (ApiController.cs:3121-3126; InsertionCode.Empty is ' '). */
export function insertionCodeToString(code: number | undefined): string {
  if (code === undefined || code === 0 || code === 32) return '';
  return String.fromCharCode(code);
}
export function insertionCodeToByte(insCode: string): number | undefined {
  const c = insCode.trim();
  return c ? c.charCodeAt(0) : undefined;
}

// --- readable filters (mapping.py:288-349) -------------------------------------------------------------------------

export interface ReadableOptions {
  /** The entry uploads as SDF/MOL/XYZ: the server gives its atoms no chain or residue, so only atom positions select (mapping.py:352-390). */
  sdfLike?: boolean;
  currentModelSerial?: number;
}

/**
 * The complete filter list for a component of entry `entrySerial` (SERVER serial) that selects exactly `atomIndices`
 * (0-based into `model`), preferring filters a person would pick in the v2 UI (mapping.py:327-349): the whole entry,
 * whole substructures, whole chains, chains + substructure, residue names, whole residues per chain (joined by
 * Union), then EnumeratedAtoms by file atom serial. Every candidate is run through evaluateFilters and dropped
 * unless it selects exactly the target. The scope is always [EntrySerial, CurrentModel], as the server's own
 * default components (AddDefaultComponents.cs:122-215).
 */
export function readableFilters(model: NanomeModel, entrySerial: number, atomIndices: Iterable<number>, options: ReadableOptions = {}): FilterModel[] {
  const target = new Set(atomIndices);
  const scope: FilterModel[] = [{ kind: 'EntrySerial', entrySerial }, { kind: 'CurrentModel' }];
  if (target.size === model.atomCount) return scope;
  const fileSerials = () => [...target].map(i => model.atomSerial[i]!);
  const fallback: FilterModel[][] = [[{ kind: 'EnumeratedAtoms', serialRangeSet: rangeSet(fileSerials()) }]];
  if (options.sdfLike) return [...scope, ...fallback[0]!];
  const ctx: EvalContext = { entrySerial, currentModelSerial: options.currentModelSerial ?? 1 };
  for (const pipelines of [...candidateFilters(model, target), fallback]) {
    const [first, ...rest] = pipelines;
    const filters: FilterModel[] = [...scope, ...first!, ...rest.map(p => ({ kind: 'Union', containedFilters: [...scope, ...p] }) as FilterModel)];
    let got: Set<number>;
    try { got = evaluateFilters(filters, model, ctx); } catch { continue; }
    if (sameSet(got, target)) return filters;
  }
  throw new BridgeError('components', 'explicit atom serials failed to select their own atoms (duplicate atom serials in the file?)');
}

/** Only the classes v2 is sure to agree on (mapping.py:218-220): Protein, Nucleic, Solvent. */
const READABLE_SUBSTRUCTURES = ['Protein', 'Nucleic', 'Solvent'] as const;

/** Readable ways to select `target`, best first; each is a list of pipelines whose selections are unioned (mapping.py:288-313). */
function* candidateFilters(model: NanomeModel, target: Set<number>): Generator<FilterModel[][]> {
  for (const name of READABLE_SUBSTRUCTURES) yield [[{ kind: 'Substructure', substructureType: name }]];

  const chains = sortedUnique([...target].map(i => model.chains.name[model.chainIndex[i]!]!));
  yield [[{ kind: 'ChainNames', names: chains }]];
  for (const name of READABLE_SUBSTRUCTURES) yield [[{ kind: 'ChainNames', names: chains }, { kind: 'Substructure', substructureType: name }]];

  const resNames = sortedUnique([...target].map(i => model.residues.name[model.residueIndex[i]!]!));
  if (resNames.every(n => /^[\x20-\x7e]+$/.test(n))) yield [[{ kind: 'ResidueNames', names: resNames }]];

  // Whole residues: one pipeline per chain, since residue numbers repeat across chains (mapping.py:306-313).
  const byChain = new Map<string, Set<number>>();
  for (const i of target) {
    const c = model.chains.name[model.chainIndex[i]!]!;
    let s = byChain.get(c);
    if (!s) { s = new Set(); byChain.set(c, s); }
    s.add(model.residueIndex[i]!);
  }
  yield chains.map(c => [
    { kind: 'ChainNames', names: [c] },
    { kind: 'ResidueSerials', serialRangeSet: rangeSet([...byChain.get(c)!].map(r => model.residues.serial[r]!)) },
  ] as FilterModel[]);
  // Insertion codes make ResidueSerials ambiguous; Residues carries them (FilterModel.cs:47).
  if ([...byChain.values()].some(rs => [...rs].some(r => (model.residues.insCode[r] ?? '').trim() !== ''))) {
    yield chains.map(c => [
      { kind: 'ChainNames', names: [c] },
      { kind: 'Residues', residues: [...byChain.get(c)!].sort((a, b) => a - b).map(r => {
        const code = insertionCodeToByte(model.residues.insCode[r] ?? '');
        return code === undefined ? { residueSerial: model.residues.serial[r]! } : { residueSerial: model.residues.serial[r]!, insertionCode: code };
      }) },
    ] as FilterModel[]);
  }
}

const sortedUnique = (xs: string[]) => [...new Set(xs)].sort();
function sameSet(a: Set<number>, b: Set<number>): boolean {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}

// --- normalisation for the wire ------------------------------------------------------------------------------------

/** Server-side formats whose atoms carry no chain or residue (SdfModelExtractor.cs:37-41; .mol/.sd/.mol2 upload as .sdf; xyz has none either). */
export const SDF_LIKE_EXTENSIONS: ReadonlySet<string> = new Set(['sdf', 'sd', 'mol', 'mol2', 'xyz']);
export const isSdfLike = (ext: string) => SDF_LIKE_EXTENSIONS.has(ext.toLowerCase());

/** 'RRGGBB' without '#' or '0x', upper-case (HexColorConverter.cs:14-18 parses with Convert.ToInt32(s, 16)). */
export function apiColor(c: string): string {
  return c.replace(/^#/, '').replace(/^0x/i, '').toUpperCase();
}

const COLORING_PROPS: Record<string, readonly (keyof ColoringModel)[]> = {
  ElementType: ['kind', 'carbonsOnly'],
  Property: ['kind', 'carbonsOnly', 'propertySource', 'paletteType', 'colors'],
  ResidueType: ['kind', 'carbonsOnly'],
  SecondaryStructure: ['kind', 'carbonsOnly'],
  Uniform: ['kind', 'carbonsOnly', 'uniformColor'],
  Enumerated: ['kind', 'carbonsOnly', 'colorRanges'],
  HydrogenBonding: ['kind', 'carbonsOnly'],
  Electrostatic: ['kind', 'carbonsOnly', 'rampMin', 'rampMax'],
};

/** Coloring in server shape: carbonsOnly present, required fields per kind filled with the server defaults, colours without '#'. */
export function normalizeColoring(c: ColoringModel | undefined): ColoringModel {
  const src: ColoringModel = c ?? { kind: 'ElementType', carbonsOnly: false };
  const out: ColoringModel = { kind: src.kind, carbonsOnly: src.carbonsOnly ?? false };
  const allowed = new Set<string>(COLORING_PROPS[src.kind] ?? ['kind', 'carbonsOnly']);
  if (allowed.has('propertySource')) {
    out.propertySource = src.propertySource ?? 'ChainInstance';
    if (src.paletteType !== undefined) out.paletteType = src.paletteType;
    if (src.colors !== undefined) out.colors = src.colors.map(apiColor);
  }
  if (allowed.has('uniformColor')) out.uniformColor = apiColor(src.uniformColor ?? 'FFFFFF'); // Uniform without uniformColor is white (ApiController VisualizationConverter)
  if (allowed.has('colorRanges')) out.colorRanges = (src.colorRanges ?? []).map(r => ({ color: apiColor(r.color), serials: { ranges: r.serials.ranges, sets: r.serials.sets } }));
  if (allowed.has('rampMin')) { out.rampMin = src.rampMin ?? -5; out.rampMax = src.rampMax ?? 5; } // Coloring.cs:38-39 ElectrostaticDensity(min -5, max 5)
  return out;
}

const VISUALIZATION_PROPS: Record<string, readonly (keyof VisualizationModel)[]> = {
  Atomistic: ['kind', 'visible', 'size', 'coloring', 'renderAtom', 'renderBond', 'bondScale', 'renderHydrogen'],
  MolecularSurface: ['kind', 'visible', 'size', 'coloring', 'opacity', 'subsurface', 'wireframe'],
  Ribbon: ['kind', 'visible', 'size', 'coloring', 'ribbonStyle'],
  ResidueLabel: ['kind', 'visible', 'size', 'coloring'],
  Interaction: ['kind', 'visible', 'size', 'coloring', 'interactionType', 'bondScale', 'gapScale', 'ligandToPocketOnly'],
};

/**
 * Visualization in server shape: `size` (NoSize when missing, pipeline.js:305-312; mara/nanome/tools.py:432-440) and
 * `coloring` always present, the fields the schema requires per kind filled with the server defaults
 * (dto.ts VISUALIZATION_DEFAULTS), fields the kind does not own stripped (schema additionalProperties:false).
 */
export function normalizeVisualization(v: VisualizationModel): VisualizationModel {
  const out: VisualizationModel = {
    kind: v.kind,
    visible: v.visible ?? true,
    size: v.size ? { sizeOption: v.size.sizeOption, scale: v.size.scale } : { ...NO_SIZE },
    coloring: normalizeColoring(v.coloring),
  };
  const allowed = new Set<string>(VISUALIZATION_PROPS[v.kind] ?? ['kind', 'visible', 'size', 'coloring']);
  const copy = <K extends keyof VisualizationModel>(k: K, fallback?: VisualizationModel[K]) => {
    if (!allowed.has(k)) return;
    const val = v[k] !== undefined ? v[k] : fallback;
    if (val !== undefined) (out as unknown as Record<string, unknown>)[k] = val;
  };
  copy('renderAtom', VISUALIZATION_DEFAULTS.renderAtom);
  copy('renderBond', VISUALIZATION_DEFAULTS.renderBond);
  copy('bondScale', VISUALIZATION_DEFAULTS.bondScale);
  copy('renderHydrogen');
  copy('opacity', VISUALIZATION_DEFAULTS.opacity);
  copy('subsurface', VISUALIZATION_DEFAULTS.subsurface);
  copy('wireframe');
  copy('ribbonStyle', VISUALIZATION_DEFAULTS.ribbonStyle);
  copy('interactionType', VISUALIZATION_DEFAULTS.interactionType);
  copy('gapScale', 0);
  copy('ligandToPocketOnly');
  return out;
}

const FILTER_PROPS: Record<string, readonly (keyof FilterModel)[]> = {
  EntrySerial: ['kind', 'entrySerial'],
  Substructure: ['kind', 'substructureType'],
  ConnectedResidue: ['kind'],
  CurrentModel: ['kind'],
  ModelSerials: ['kind', 'serialRangeSet', 'onlyWhenSceneModelMatches'],
  EnumeratedAtoms: ['kind', 'serialRangeSet'],
  ResidueNames: ['kind', 'names'],
  AtomNames: ['kind', 'names'],
  ChainNames: ['kind', 'names'],
  ResidueSerials: ['kind', 'serialRangeSet'],
  Residues: ['kind', 'residues'],
  Distance: ['kind', 'distance', 'wholeResidue', 'ignoreSelf', 'containedFilters'],
  Union: ['kind', 'containedFilters'],
  Intersect: ['kind', 'containedFilters'],
  Except: ['kind', 'containedFilters'],
  AltLocs: ['kind', 'occupancySelection'],
};

/** Filter with only the fields its kind owns (schema additionalProperties:false), recursively. */
export function normalizeFilter(f: FilterModel): FilterModel {
  const allowed = FILTER_PROPS[f.kind];
  if (!allowed) return { ...f, ...(f.containedFilters ? { containedFilters: f.containedFilters.map(normalizeFilter) } : {}) };
  const out: FilterModel = { kind: f.kind };
  for (const k of allowed) {
    if (k === 'kind') continue;
    const val = f[k];
    if (val !== undefined) (out as unknown as Record<string, unknown>)[k] = k === 'containedFilters' ? (val as FilterModel[]).map(normalizeFilter) : val;
  }
  if (f.kind === 'Distance') {
    out.wholeResidue ??= false;
    out.ignoreSelf ??= false;
    out.containedFilters ??= [];
  }
  return out;
}

const hasKind = (filters: FilterModel[], kinds: ReadonlySet<string>): boolean =>
  filters.some(f => kinds.has(f.kind) || (f.containedFilters ? hasKind(f.containedFilters, kinds) : false));

const FREE_FORM = new Set(['EnumeratedAtoms']);
const CHAIN_RESIDUE_KINDS = new Set(['ChainNames', 'ResidueNames', 'ResidueSerials', 'Residues', 'AtomNames', 'Substructure', 'AltLocs']);

// --- the conversion -------------------------------------------------------------------------------------------------

export interface ComponentsForServerResult {
  /** ComponentInput bodies, in order, one per POST .../components/add. */
  send: ComponentInput[];
  /** Indexes into `send` to hide with toggle-hidden once added (the add body has no `hidden` field). */
  hiddenIndexes: number[];
  warnings: string[];
}

/**
 * Lite components -> server payloads. For every component: entry serials remapped (also nested), Interaction
 * visualizations dropped (the server computes its own), visualizations normalised, free-form selections
 * (EnumeratedAtoms) re-expressed as readable filters, SDF/MOL/XYZ selections as atom positions. A component is
 * hidden when Lite hides it or when every visualization is off (then they are sent on, pipeline.js:314-332).
 *
 * @param serialMap local entry serial -> server entry serial (from the load-multiple answers)
 * @param models local entry serial -> NanomeModel (needed only for components with free-form selections)
 */
export function componentsForServer(components: readonly Component[], entries: readonly Entry[], serialMap: Map<number, number>, models: Map<number, NanomeModel>): ComponentsForServerResult {
  const send: ComponentInput[] = [];
  const hiddenIndexes: number[] = [];
  const warnings: string[] = [];
  const entryBySerial = new Map(entries.map(e => [e.serial, e]));

  for (const component of components) {
    const label = `component "${component.name}"`;
    let visualizations = component.visualizations.filter(v => v.kind !== 'Interaction');
    if (visualizations.length < component.visualizations.length) warnings.push(`${label}: Interaction visualizations are not sent (the server computes interactions itself).`);
    if (!visualizations.length) { warnings.push(`${label}: skipped, it has no visualizations the server can store.`); continue; }
    visualizations = visualizations.map(normalizeVisualization);

    const localSerials = component.entrySerials?.length ? component.entrySerials : filterEntrySerials(component.filters);
    const single = localSerials.length === 1 ? localSerials[0]! : undefined;
    const entry = single !== undefined ? entryBySerial.get(single) : undefined;
    const model = single !== undefined ? models.get(single) : undefined;
    const sdfLike = entry ? isSdfLike(entry.source.ext) : false;
    const needsRewrite = hasKind(component.filters, FREE_FORM) || (sdfLike && hasKind(component.filters, CHAIN_RESIDUE_KINDS));

    let filters: FilterModel[];
    if (needsRewrite && single !== undefined && entry && model) {
      const serverSerial = serialMap.get(single);
      if (serverSerial === undefined) throw new BridgeError('components', `${label} selects entry ${single}, which is not one of the ${serialMap.size} loaded structures`);
      try {
        const target = evaluateFilters(component.filters, model, { entrySerial: single, currentModelSerial: entry.currentModelSerial });
        filters = readableFilters(model, serverSerial, target, { sdfLike, currentModelSerial: entry.currentModelSerial });
      } catch (e) {
        if (!(e instanceof NotEvaluable)) throw e;
        warnings.push(`${label}: ${e.message}; sent as it is.`);
        filters = remapFilters(component.filters, serialMap, `${label} filters`).map(normalizeFilter);
      }
    } else {
      if (needsRewrite) warnings.push(`${label}: no model for entry ${single ?? localSerials.join(',')}; its selection is sent by atom serial as it is.`);
      filters = remapFilters(component.filters, serialMap, `${label} filters`).map(normalizeFilter);
    }

    const allOff = visualizations.every(v => v.visible === false);
    if (allOff) visualizations = visualizations.map(v => ({ ...v, visible: true }));
    if (component.hidden || allOff) hiddenIndexes.push(send.length);
    send.push({ name: component.name, filters, visualizations });
  }
  return { send, hiddenIndexes, warnings };
}
